import { z } from 'zod'

// `chunkId` is unique; the resolver hydrates fileStoreId + page from the tool-call cache.
export const ChunkCitationAgentInputSchema = z.object({
  type: z.literal('chunk'),
  chunkId: z.string().describe('Identifier of the specific chunk returned by the slice search tool'),
  anchor: z.string().trim().min(1).describe('Verbatim phrase copied from the chunk text'),
})

// Fields every chunk citation carries regardless of which slice produced it. IDs are lifted from
// `SearchableDocumentMetadata` for click-through.
const ChunkCitationBaseSchema = z.object({
  sourceType: z.literal('chunk'),
  sourceLabel: z.string(),
  fileName: z.string().optional(),
  quote: z.string(),
  fileStoreId: z.string(),
  chunkId: z.string(),
  pageNumber: z.number().int().nonnegative().optional(),
  anchor: z.string(),
  // Stable per-citation id, stamped at persist as the feedback `subOutputId`. Optional: the resolver never
  // mints it and older citations predate it.
  id: z.string().optional(),
})

// `scope` discriminates request documents (slice #1) vs supplier documents (slice #4) for the
// renderer; each scope extends the base with only the click-through identifiers it can actually
// populate, so the resolved citation can no longer carry a meaningless mix of all-optional IDs.
const RequestDocumentsChunkCitationSchema = z.object({
  ...ChunkCitationBaseSchema.shape,
  scope: z.literal('request_documents'),
  requestId: z.string(),
  requestDocumentId: z.string(),
})

const SupplierDocumentsChunkCitationSchema = z.object({
  ...ChunkCitationBaseSchema.shape,
  scope: z.literal('supplier_documents'),
  supplierId: z.string(),
  agreementIds: z.array(z.string()).optional(),
})

export const ChunkCitationResolvedSchema = z.discriminatedUnion('scope', [
  RequestDocumentsChunkCitationSchema,
  SupplierDocumentsChunkCitationSchema,
])
export type ChunkCitationResolved = z.infer<typeof ChunkCitationResolvedSchema>
